/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Callout, Checkbox } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { EventCategoryResource, EventGroupResource, DocumentTypeResource, EnvironmentResource, TenantResource, UserResource, EventAgentResource, SpaceResource, ProjectGroupResource, ProjectSummaryResource, } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import { filter, flatten, map, uniq, isEmpty } from "lodash";
import * as React from "react";
import useIsMultiTenancyEnabledFeatureFlag from "~/areas/configuration/hooks/useIsMultiTenancyEnabledFeatureFlag";
import { client, session } from "~/clientInstance";
import { EventCategoryPreviewChip } from "~/components/Chips/index";
import type { DoBusyTask } from "~/components/DataBaseComponent/DataBaseComponent";
import buildValueList from "~/components/EventFilter/buildValueList";
import { DocumentTypeMultiSelect } from "~/components/MultiSelect/DocumentTypeMultiSelect";
import { EnvironmentMultiSelect } from "~/components/MultiSelect/EnvironmentMultiSelect";
import EventAgentMultiSelect from "~/components/MultiSelect/EventAgentMultiSelect";
import { EventCategoryMultiSelect } from "~/components/MultiSelect/EventCategoryMultiSelect";
import { EventGroupMultiSelect } from "~/components/MultiSelect/EventGroupMultiSelect";
import { ProjectGroupMultiSelect } from "~/components/MultiSelect/ProjectGroupMultiSelect";
import { ProjectMultiSelect } from "~/components/MultiSelect/ProjectMultiSelect";
import { SpaceMultiSelect } from "~/components/MultiSelect/SpaceMultiSelect";
import { TargetTagMultiSelect } from "~/components/MultiSelect/TargetTagMultiSelect";
import { TenantMultiSelect } from "~/components/MultiSelect/TenantMultiSelect";
import { TenantTagMultiSelect } from "~/components/MultiSelect/TenantTagMultiSelect";
import { UserMultiSelect } from "~/components/MultiSelect/UserMultiSelect";
import { PermissionCheck } from "~/components/PermissionCheck";
export interface EventFilterSelections {
    Environments: string[];
    Users: string[];
    Projects: string[];
    ProjectGroups: string[];
    EventCategories: string[];
    EventGroups: string[];
    EventAgents: string[];
    DocumentTypes: string[];
    Tenants: string[];
    Tags: string[];
}
export interface EventFilterProps {
    doBusyTask: DoBusyTask;
    // Supply the collections of resources for each filter you wish to support
    eventCategories: EventCategoryResource[];
    eventGroups: EventGroupResource[];
    eventAgents?: EventAgentResource[];
    environments: EnvironmentResource[];
    spaces?: SpaceResource[];
    //  (leave it undefined and it will be hidden).
    documentTypes?: DocumentTypeResource[];
    projects?: ProjectSummaryResource[];
    projectGroups?: ProjectGroupResource[];
    roles?: string[];
    tenants?: TenantResource[];
    users?: UserResource[];
    includeSystem?: boolean;
    // Supply the selected values for each filter.
    selectedEventCategories: string[];
    selectedEventGroups: string[];
    selectedEventAgents?: string[];
    selectedEnvironments: string[];
    selectedSpaces?: string[];
    selectedDocumentTypes?: string[];
    selectedProjects?: string[];
    selectedProjectGroups?: string[];
    selectedRoles?: string[];
    canAddRoles?: boolean;
    selectedTenants?: string[];
    selectedTenantTags?: string[];
    selectedUsers?: string[];
    // Supply an onChange event for each filter.
    onChangeEventCategories(newValue: string[]): void;
    onChangeEventGroups(newValue: string[]): void;
    onChangeEventAgents?(newValue: string[]): void;
    onChangeEnvironments(newValue: string[]): void;
    onChangeSpaces?(newValue: string[]): void;
    onChangeDocumentTypes?(newValue: string[]): void;
    onChangeProjects?(newValue: string[]): void;
    onChangeProjectGroups?(newValue: string[]): void;
    onChangeRoles?(newValue: string[]): void;
    onChangeTenants?(newValue: string[]): void;
    onChangeTenantTags?(newValue: string[]): void;
    onChangeUsers?(newValue: string[]): void;
    onIncludeSystemChange?(newValue: boolean): void;
}
export function EventFilter(props: EventFilterProps) {
    const isMultiTenancyEnabled = useIsMultiTenancyEnabledFeatureFlag();
    const renderSpaceSpecificSelectors = () => {
        // These are linked to access in the current space, because that's where the data will come from
        // we need to revisit how these will work going forward to make the filtering easier to do cross-space
        const isWithinASpace = client.spaceId;
        return (isWithinASpace && (<>
                    {props.projects && props.onChangeProjects && props.selectedProjects && <ProjectMultiSelect items={props.projects} onChange={props.onChangeProjects} value={props.selectedProjects}/>}

                    {props.projectGroups && props.onChangeProjectGroups && props.selectedProjectGroups && <ProjectGroupMultiSelect items={props.projectGroups} onChange={props.onChangeProjectGroups} value={props.selectedProjectGroups}/>}

                    <EnvironmentMultiSelect environments={props.environments} onChange={props.onChangeEnvironments} value={props.selectedEnvironments}/>

                    {props.roles && props.onChangeRoles && props.selectedRoles && <TargetTagMultiSelect items={props.roles} onChange={props.onChangeRoles} value={props.selectedRoles} canAdd={!!props.canAddRoles} analyticsArea="Event Filter"/>}

                    {isMultiTenancyEnabled && (<PermissionCheck permission={Permission.TenantView} tenant="*">
                            {props.tenants && props.onChangeTenants && props.selectedTenants && <TenantMultiSelect items={props.tenants} onChange={props.onChangeTenants} value={props.selectedTenants}/>}
                            {props.onChangeTenantTags && props.selectedTenantTags && <TenantTagMultiSelect onChange={props.onChangeTenantTags} doBusyTask={props.doBusyTask} value={props.selectedTenantTags}/>}
                        </PermissionCheck>)}
                </>));
    };
    const createEventGroupCategories = (selectedEventGroups: string[]) => {
        const selectedGroups = filter(props.eventGroups, (eventGroup: EventGroupResource) => {
            return selectedEventGroups.includes(eventGroup.Id);
        });
        const selectedEventCategoryIds = uniq<string>(flatten<string>(map(selectedGroups, (selectedGroup: EventGroupResource) => {
            return selectedGroup.EventCategories;
        })));
        return props.eventCategories.filter((ec) => selectedEventCategoryIds.includes(ec.Id)).map((ec) => <EventCategoryPreviewChip key={ec.Id} eventCategory={ec}/>);
    };
    const hasSystemEventView = session.currentPermissions!.scopeToSystem().hasPermissionInAnyScope(Permission.EventView);
    return (<div>
            {hasSystemEventView && props.includeSystem !== undefined && <Checkbox value={props.includeSystem} onChange={props.onIncludeSystemChange || noOp} label="Include system events"/>}
            <SpaceSelector spaces={props.spaces} selectedSpaces={props.selectedSpaces ?? []} onChangeSpaces={props.onChangeSpaces}/>
            <div>
                <EventGroupMultiSelect items={props.eventGroups} onChange={props.onChangeEventGroups} value={props.selectedEventGroups}/>
                {!isEmpty(props.selectedEventGroups) && <span>Events included:</span>}
                {buildValueList(createEventGroupCategories(props.selectedEventGroups))}
            </div>

            <EventCategoryMultiSelect items={props.eventCategories} onChange={props.onChangeEventCategories} value={props.selectedEventCategories}/>

            {props.documentTypes && props.onChangeDocumentTypes && props.selectedDocumentTypes && <DocumentTypeMultiSelect items={props.documentTypes} onChange={props.onChangeDocumentTypes} value={props.selectedDocumentTypes}/>}

            {props.users && props.users.length > 0 && props.onChangeUsers && props.selectedUsers && <UserMultiSelect items={props.users} onChange={props.onChangeUsers} value={props.selectedUsers}/>}

            {props.eventAgents && props.onChangeEventAgents && props.selectedEventAgents && <EventAgentMultiSelect items={props.eventAgents} onChange={props.onChangeEventAgents} value={props.selectedEventAgents}/>}

            {renderSpaceSpecificSelectors()}
        </div>);
}
interface SpaceSelectorProps {
    selectedSpaces: string[];
    spaces?: SpaceResource[];
    onChangeSpaces?: (newValue: string[]) => void;
}
function SpaceSelector({ spaces, selectedSpaces, onChangeSpaces }: SpaceSelectorProps) {
    const hasEventViewInAnySpace = session.currentPermissions!.hasPermissionInAnyScope(Permission.EventView);
    if (!hasEventViewInAnySpace) {
        return (<div style={{ margin: `${space[16]} 0 0 0` }}>
                <Callout type={"information"} title={"Permission required"}>
                    You do not have {Permission.EventView} permission in any given Space.
                </Callout>
            </div>);
    }
    const ready = spaces && spaces.length > 0;
    return ready ? <SpaceMultiSelect items={spaces || []} onChange={onChangeSpaces || noOp} value={selectedSpaces}/> : null;
}
